import React from 'react';
import PropTypes from 'prop-types';
import { Clear } from '@material-ui/icons';
import { CSSTransition } from 'react-transition-group';
import { StringConstantsMixin } from 'react_components/mixins/shared/string_constants_mixin';
import { checkLinkAndAppendLanguage } from 'utils/localization';
import UserPropType from 'proptypes/UserPropType';
import useIpCountryCode from 'hooks/useIpCountryCode';
import { getShopLink } from 'utils/Links';
import LinkPropType from 'proptypes/LinkPropType';
import * as styles from './styles/styles.module.scss';
import LinkDropdown from '../shared/LinkDropdown';
import MainButton from '../shared/MainButton';

const { MY_ACCOUNT, UPGRADE_TO_PRO } = StringConstantsMixin.strings;

function updateLinkRef(linkPath) {
  return linkPath.replace('?ref=header', '?ref=mobile-header');
}

function updateDropdownLinkRef(link) {
  if (!link.dropdown) {
    return undefined;
  }

  return link.dropdown.map(dropdownLink => ({ ...dropdownLink, linkPath: updateLinkRef(dropdownLink.linkPath) }));
}

function parseLinkRef(links) {
  if (!links) {
    return undefined;
  }

  return links.map(link => ({ ...link, dropdown: updateDropdownLinkRef(link), linkPath: updateLinkRef(link.linkPath) }));
}

function SideDrawer({ currentUser, featureFlags, isOpen, links, toggleIsOpen, user }) {
  const ipCountryCode = useIpCountryCode();

  function renderSignInAndLoginButtons() {
    const { login, signup } = user;
    return (
      <div className={styles.sideDrawerButtons}>
        <MainButton href={signup.linkPath} text={signup.label} />
        <MainButton type="outline" href={login.linkPath} text={login.label} />
      </div>
    );
  }

  function renderCtaButtons() {
    if (!user.display) {
      return renderSignInAndLoginButtons();
    }

    if (currentUser && !currentUser.pro) {
      return (
        <div className={styles.sideDrawerButtons}>
          <MainButton href={checkLinkAndAppendLanguage('/pro?ref=header')} text={UPGRADE_TO_PRO} />
        </div>
      );
    }

    return null;
  }

  const { explore, help, downloadApp, community, saved } = links;
  const dropdownLinks = parseLinkRef(featureFlags.communityFeatureFlag ? [explore, community, saved] : [explore, saved]);

  dropdownLinks.push(getShopLink(ipCountryCode));

  // if a user is logged in, then these are defined in HeaderHelper.js
  if (user.dropdown.length > 0) {
    const myAccount = { dropdown: user.dropdown, label: MY_ACCOUNT, linkPath: '#' };
    dropdownLinks.push(myAccount);
  }

  return (
    <>
      <CSSTransition
        in={isOpen}
        classNames={{
          enterDone: styles.slideNavigationEnterDone,
          exitDone: styles.slideNavigationExitDone
        }}
        timeout={0}
      >
        <div className={styles.drawerContainer}>
          <a href="#" onClick={toggleIsOpen} className={styles.exitButton}>
            <Clear />
          </a>
          <div className={styles.linkHeadersContainer}>
            {dropdownLinks.map(linkProps => (
              <LinkDropdown key={linkProps.label} {...linkProps} data-testid={linkProps.label} />
            ))}
          </div>
          <div className={styles.helpAndDownloadContainer}>
            {[help, downloadApp].map(link => (
              <a key={link.label} title={link.label} href={link.linkPath} className={styles.helpDownloadLinks} data-testid={link.label}>
                {link.label}
              </a>
            ))}
          </div>
          {renderCtaButtons()}
        </div>
      </CSSTransition>
      <CSSTransition
        in={isOpen}
        classNames={{
          enter: styles.opacCoverEnter,
          enterDone: styles.opacCoverEnterDone,
          exit: styles.opacCoverExit,
          exitDone: styles.opacCoverExitDone
        }}
        timeout={100}
      >
        <div className={styles.opacCover} onClick={toggleIsOpen} />
      </CSSTransition>
    </>
  );
}

SideDrawer.defaultProps = {
  currentUser: null
};

SideDrawer.propTypes = {
  currentUser: UserPropType,
  user: PropTypes.shape({
    name: PropTypes.string,
    linkPath: PropTypes.string,
    imagePath: PropTypes.string,
    reputation: PropTypes.number,
    display: PropTypes.bool,
    login: LinkPropType,
    signup: LinkPropType
  }).isRequired,
  links: PropTypes.shape({
    community: LinkPropType,
    downloadApp: LinkPropType,
    explore: LinkPropType,
    help: LinkPropType,
    saved: LinkPropType
  }).isRequired
};

export default SideDrawer;
